import {
  Condition,
  Conditional,
  isRule,
  MatchValue,
  Operator,
  OPERATORS,
  Rule
} from '@dadohr/shared/domainTypes/rules';
import { TFunction } from 'i18next';
import { isEqual, values } from 'lodash';
import moment from 'moment-timezone';
import {
  OPERATORS_BY_DATA_TYPE,
  BLANK_LABEL_STRING,
  BLANK_LABEL_USER,
  BLANK_VALUE,
  DEFAULT_DATE_FORMAT,
  DEFAULT_VALUE_BY_DATA_TYPE,
  EMPTY_RULE_GROUP,
  isBlankOperator,
  OPERATORS_TO_STRING,
  usesSetOperators
} from './constants';
import {
  DateOperator,
  FormattedRuleElement,
  RuleBuilderConfig,
  RuleBuilderConfigItem,
  RuleDataType,
  StringOperator
} from './types';

// ============================================================================
// RULE CREATION & VALIDATION UTILITIES
// ============================================================================

/**
 * Helper function to create expanded rules from string operators with multiple values
 */
export const createExpandedRule = <T extends string>(
  key: T,
  operator: StringOperator,
  values: string[]
): Rule<T> => {
  if (values.length === 0) {
    throw new Error('Cannot create expanded rule with no values');
  }

  // Handle blank values specially
  const conditions: Condition<T>[] = [];
  const regularValues = values.filter((v) => v !== '__BLANK__');
  const hasBlank = values.includes('__BLANK__');

  // Add regular value conditions
  if (regularValues.length > 0) {
    const originalOperator = mapStringOperatorToOriginal(operator);

    conditions.push(
      ...regularValues.map((value) => ({
        k: key,
        op: originalOperator,
        v: value
      }))
    );
  }

  // Add blank condition if present
  if (hasBlank) {
    const blankOperator = operator === 'isAnyOf' ? 'isBlank' : 'isNotBlank';
    conditions.push({
      k: key,
      op: blankOperator as Operator,
      v: ''
    });
  }

  // Determine the rule type based on operator
  const ruleType: Conditional =
    operator === 'isAnyOf' || operator === 'containsAnyOf' ? 'any' : 'all';

  return {
    type: ruleType,
    els: conditions
  };
};

/**
 * Helper function to map new string operators to original operators
 */
export const mapStringOperatorToOriginal = (
  operator: StringOperator
): Operator => {
  switch (operator) {
    case 'isAnyOf':
      return '==';
    case 'isNoneOf':
      return '!=';
    case 'containsAnyOf':
      return '=~';
    case 'doesNotContainAnyOf':
      return '!~';
    default:
      return '==' as Operator;
  }
};

/**
 * Helper function to create an expanded rule from the "isOn" date operator.
 * "Field is on <day>" becomes "Field is on or after <day 00:00> AND Field is before <next day 00:00>"
 * to account for date fields actually being stored as date-times.
 */
export const createExpandedDateRule = <T extends string>(
  key: T,
  value: number,
  tz: string
): Rule<T> => {
  const startOfDay = moment.tz(value, tz).startOf('day');
  const startOfNextDay = startOfDay.clone().add(1, 'day');

  return {
    type: 'all',
    els: [
      { k: key, op: '>=' as Operator, v: startOfDay.valueOf() },
      { k: key, op: '<' as Operator, v: startOfNextDay.valueOf() }
    ]
  };
};

/**
 * Helper function to create an expanded rule from the "isNotOn" date operator.
 * "Field is not on <day>" is the negation of "is on" — by De Morgan's law it
 * becomes "Field is before <day 00:00> OR Field is on or after <next day 00:00>",
 * mirroring how isNoneOf negates isAnyOf (AND of != vs OR of ==).
 */
export const createExpandedNotOnDateRule = <T extends string>(
  key: T,
  value: number,
  tz: string
): Rule<T> => {
  const startOfDay = moment.tz(value, tz).startOf('day');
  const startOfNextDay = startOfDay.clone().add(1, 'day');

  return {
    type: 'any',
    els: [
      { k: key, op: '<' as Operator, v: startOfDay.valueOf() },
      { k: key, op: '>=' as Operator, v: startOfNextDay.valueOf() }
    ]
  };
};

/**
 * Helper function to detect if a rule represents an expanded "isOn"/"isNotOn"
 * date condition. Both patterns compare the same pair of day-boundary values;
 * they differ only in which operator holds the earlier boundary and whether
 * the rule is an AND (isOn: value is inside the range) or an OR (isNotOn:
 * value is outside the range).
 */
export const isExpandedDateRule = <T extends string>(
  rule: Rule<T>,
  config: RuleBuilderConfig<T>
): {
  isExpanded: boolean;
  key?: T;
  value?: number;
  operator?: DateOperator;
} => {
  if (rule.els.length !== 2) {
    return { isExpanded: false };
  }

  const allConditions = rule.els.every((el) => !isRule(el));
  if (!allConditions) {
    return { isExpanded: false };
  }

  const conditions = rule.els as Condition<T>[];
  const key = conditions[0].k;
  const fieldConfig = config[key];

  if (
    !fieldConfig ||
    fieldConfig.dataType !== 'date' ||
    !conditions.every((cond) => cond.k === key)
  ) {
    return { isExpanded: false };
  }

  const gte = conditions.find((cond) => cond.op === '>=');
  const lt = conditions.find((cond) => cond.op === '<');

  if (!gte || !lt) {
    return { isExpanded: false };
  }

  const tz = fieldConfig.options.tz;

  if (rule.type === 'all') {
    // isOn: gte holds the day start, lt holds the start of the next day
    const expectedNextDay = moment
      .tz(gte.v as number, tz)
      .startOf('day')
      .add(1, 'day')
      .valueOf();

    if (lt.v !== expectedNextDay) {
      return { isExpanded: false };
    }

    return { isExpanded: true, key, value: gte.v as number, operator: 'isOn' };
  }

  if (rule.type === 'any') {
    // isNotOn: lt holds the day start, gte holds the start of the next day
    const expectedNextDay = moment
      .tz(lt.v as number, tz)
      .startOf('day')
      .add(1, 'day')
      .valueOf();

    if (gte.v !== expectedNextDay) {
      return { isExpanded: false };
    }

    return {
      isExpanded: true,
      key,
      value: lt.v as number,
      operator: 'isNotOn'
    };
  }

  return { isExpanded: false };
};

/**
 * Helper function to detect if a rule represents an expanded string condition
 */
export const isExpandedStringRule = <T extends string>(
  rule: Rule<T>,
  config: RuleBuilderConfig<T>
): {
  isExpanded: boolean;
  key?: T;
  operator?: StringOperator;
  values?: string[];
} => {
  // Must have at least one element
  if (rule.els.length === 0) {
    return { isExpanded: false };
  }

  // All elements must be conditions (not nested rules)
  const allConditions = rule.els.every((el) => !isRule(el));
  if (!allConditions) {
    return { isExpanded: false };
  }

  const conditions = rule.els as Condition<T>[];

  // If there's only one condition and it has an empty array, it's NOT an expanded rule
  if (conditions.length === 1) {
    const singleCondition = conditions[0];

    if (Array.isArray(singleCondition.v) && singleCondition.v.length === 0) {
      return { isExpanded: false };
    }
  }

  // Check if all conditions are for the same key and it's a set-operator field
  const firstCondition = conditions[0];
  const key = firstCondition.k;

  if (!config[key] || !usesSetOperators(config[key].dataType)) {
    return { isExpanded: false };
  }

  // All conditions must be for the same key
  const allSameKey = conditions.every((cond) => cond.k === key);
  if (!allSameKey) {
    return { isExpanded: false };
  }

  // Separate blank and non-blank conditions
  const blankConditions = conditions.filter(
    (cond) => cond.op === 'isBlank' || cond.op === 'isNotBlank'
  );
  const valueConditions = conditions.filter(
    (cond) =>
      cond.op === '==' ||
      cond.op === '!=' ||
      cond.op === '=~' ||
      cond.op === '!~'
  );

  // Must have at least one actual condition with values
  if (valueConditions.length === 0 && blankConditions.length === 0) {
    return { isExpanded: false };
  }

  // Check for valid patterns
  let stringOperator: StringOperator | null = null;
  let values: string[] = [];

  // Pattern 1: All == with OR rule type (isAnyOf)
  if (
    rule.type === 'any' &&
    valueConditions.every((c) => c.op === '==') &&
    (blankConditions.length === 0 ||
      blankConditions.every((c) => c.op === 'isBlank'))
  ) {
    stringOperator = 'isAnyOf';
    values = [...valueConditions.map((c) => c.v as string)];
    if (blankConditions.length > 0) values.push('__BLANK__');
  }
  // Pattern 2: All != with AND rule type (isNoneOf)
  else if (
    rule.type === 'all' &&
    valueConditions.every((c) => c.op === '!=') &&
    (blankConditions.length === 0 ||
      blankConditions.every((c) => c.op === 'isNotBlank'))
  ) {
    stringOperator = 'isNoneOf';
    values = [...valueConditions.map((c) => c.v as string)];
    if (blankConditions.length > 0) values.push('__BLANK__');
  }
  // Pattern 3: All =~ with OR rule type (containsAnyOf)
  else if (
    rule.type === 'any' &&
    valueConditions.every((c) => c.op === '=~') &&
    blankConditions.length === 0
  ) {
    stringOperator = 'containsAnyOf';
    values = valueConditions.map((c) => c.v as string);
  }
  // Pattern 4: All !~ with AND rule type (doesNotContainAnyOf)
  else if (
    rule.type === 'all' &&
    valueConditions.every((c) => c.op === '!~') &&
    blankConditions.length === 0
  ) {
    stringOperator = 'doesNotContainAnyOf';
    values = valueConditions.map((c) => c.v as string);
  } else {
    console.log('🔍 No pattern matches');
  }

  // Only consider it expanded if we have actual values
  if (stringOperator && values.length > 0) {
    return {
      isExpanded: true,
      key,
      operator: stringOperator,
      values
    };
  }

  return { isExpanded: false };
};

export const getEmptyDefaultCondition = <T extends string>(
  config: RuleBuilderConfig<T>
): Condition<T> => {
  const c = values(config)[0] as RuleBuilderConfigItem<T>;
  const { dataType, key } = c;

  let op: Operator = '==';
  if (usesSetOperators(dataType)) {
    op = 'isAnyOf' as Operator;
  } else if (dataType === 'date') {
    op = 'isOn' as Operator;
  }

  return {
    k: key,
    op,
    v: DEFAULT_VALUE_BY_DATA_TYPE[dataType]
  };
};

export const getEmptyCondition = <T extends string>(): Condition<T> => {
  return {
    k: '' as T,
    op: '' as Operator,
    v: ''
  };
};

export const createEmptyRuleGroup = <T extends string>(): Rule<T> => {
  return {
    type: 'all',
    els: [getEmptyCondition<T>()]
  };
};

export const isEmptyRuleGroup = <T extends string>(rule: Rule<T>): boolean => {
  if (rule.els.length === 0) return true;

  return rule.els.every((el) => {
    if (isRule(el)) {
      return isEmptyRuleGroup(el);
    }
    return el.k === '';
  });
};

export const countConditions = <T extends string>(rule: Rule<T>): number =>
  rule.els.reduce(
    (count, el) => count + (isRule(el) ? countConditions(el) : 1),
    0
  );

export const getCanRemove = <T extends string>(
  isRoot: boolean,
  totalConditions: number,
  conditionKey: T,
  showEmptyRowWhenNull: boolean
): boolean => {
  if (!isRoot) return true;

  if (!showEmptyRowWhenNull) return true;

  if (totalConditions > 1) return true;

  return conditionKey !== '';
};

export const isEmptyRule = (r: Rule<any>) => isEqual(r, EMPTY_RULE_GROUP);

// ============================================================================
// RULE CHANGE HANDLERS (for UI components)
// ============================================================================

/**
 * Handles changes to expanded string rules (rules that are rendered as single conditions)
 */
export const handleExpandedRuleChange = <T extends string>(
  nextCond: Condition<T> | null,
  config: RuleBuilderConfig<T>,
  originalOperator?: StringOperator | DateOperator
): Rule<T> | null => {
  if (nextCond === null) {
    return null;
  }

  // A blank operator ignores its value, so there is nothing to expand; keep
  // the single condition (the original set operator must not be re-applied).
  if (isBlankOperator(nextCond.op)) {
    return {
      type: 'all',
      els: [nextCond]
    };
  }

  const fieldConfig = config[nextCond.k];

  if (fieldConfig?.dataType === 'date') {
    if (['isOn'].includes(nextCond.op)) {
      return createExpandedDateRule(
        nextCond.k,
        nextCond.v as number,
        fieldConfig.options.tz
      );
    }
    if (['isNotOn'].includes(nextCond.op)) {
      return createExpandedNotOnDateRule(
        nextCond.k,
        nextCond.v as number,
        fieldConfig.options.tz
      );
    }
    return {
      type: 'all',
      els: [nextCond]
    };
  }

  const values = Array.isArray(nextCond.v)
    ? nextCond.v
    : [nextCond.v as string];

  // If values array is empty, keep as simple condition
  if (Array.isArray(nextCond.v) && nextCond.v.length === 0) {
    return {
      type: 'all',
      els: [nextCond]
    };
  }

  // Check if this should create an expanded rule
  const operatorToUse = originalOperator || nextCond.op; // ← CHANGE THIS
  const shouldExpand =
    usesSetOperators(config[nextCond.k]?.dataType) &&
    ['isAnyOf', 'isNoneOf', 'containsAnyOf', 'doesNotContainAnyOf'].includes(
      operatorToUse
    ) && // ← CHANGE THIS
    values.length > 0;

  if (shouldExpand) {
    return createExpandedRule(
      nextCond.k,
      nextCond.op as StringOperator,
      values
    );
  } else {
    // For non-string operators, wrap in rule group
    return {
      type: 'all',
      els: [nextCond]
    };
  }
};

const getFieldDataType = <T extends string>(
  config: RuleBuilderConfig<T>,
  item: Condition<T>
): RuleDataType | undefined => {
  return config[item.k]?.dataType;
};

const fieldConfigOnlyBlank = <T extends string>(
  config: RuleBuilderConfig<T>,
  item: Condition<T>
) => {
  const itemConfig = config[item.k];
  return (
    itemConfig?.options?.allowedOperators?.length === 2 &&
    itemConfig.options.allowedOperators.includes('isBlank') &&
    itemConfig.options.allowedOperators.includes('isNotBlank')
  );
};

/**
 * Determines if we should flatten a nested rule when changing from expanded to simple
 */
export const shouldFlattenRule = <T extends string>(
  currentElement: Rule<T> | Condition<T>,
  nextElement: Rule<T>,
  config: RuleBuilderConfig<T>
): boolean => {
  // console.log('full current element', currentElement);
  // console.log('full next element', nextElement);
  // Check if we're collapsing from an expanded rule to an empty string condition, IE deleting all items in a multi select
  const isCollapsingExpandedStringRule =
    isRule(currentElement) &&
    currentElement.els.length >= 1 &&
    !isRule(currentElement.els[0]) &&
    usesSetOperators(config[currentElement.els[0].k]?.dataType) &&
    !fieldConfigOnlyBlank(config, currentElement.els[0]) &&
    nextElement.els?.length === 1 &&
    !isRule(nextElement.els[0]) &&
    Array.isArray(nextElement.els[0].v) &&
    nextElement.els[0].v.length === 0 &&
    // Check if we're going from expanded operators to string operators
    ((currentElement.els[0].op === '==' &&
      nextElement.els[0].op === 'isAnyOf') ||
      (currentElement.els[0].op === '!=' &&
        nextElement.els[0].op === 'isNoneOf') ||
      (currentElement.els[0].op === '=~' &&
        nextElement.els[0].op === 'containsAnyOf') ||
      (currentElement.els[0].op === '!~' &&
        nextElement.els[0].op === 'doesNotContainAnyOf') ||
      (currentElement.els[0].op === 'isBlank' &&
        nextElement.els[0].op === 'isAnyOf') ||
      (currentElement.els[0].op === 'isNotBlank' &&
        nextElement.els[0].op === 'isNoneOf'));
  //console.log('collapsing expanded', isCollapsingExpandedStringRule);

  // Check if we're changing from an expanded rule to a simple condition (field type change)
  const isChangingFromExpandedToSimple =
    isRule(currentElement) &&
    isRule(nextElement) &&
    nextElement.els?.length === 1 &&
    !isRule(nextElement.els[0]) &&
    // The rule data type has changed
    !isRule(currentElement.els[0]) &&
    (usesSetOperators(getFieldDataType(config, currentElement.els[0])) ||
      getFieldDataType(config, currentElement.els[0]) === 'date') &&
    getFieldDataType(config, currentElement.els[0]) !==
      getFieldDataType(config, nextElement.els[0]) &&
    // The new condition is not a set/date operator that would create an expanded rule
    !(
      usesSetOperators(getFieldDataType(config, nextElement.els[0])) &&
      [
        'isAnyOf',
        'isNoneOf',
        'containsAnyOf',
        'doesNotContainAnyOf',
        'isBlank',
        'isNotBlank'
      ].includes(nextElement.els[0].op)
    ) &&
    !(
      getFieldDataType(config, nextElement.els[0]) === 'date' &&
      ['isOn', 'isNotOn', 'isBlank', 'isNotBlank'].includes(
        nextElement.els[0].op
      )
    ) &&
    !isExpandedStringRule(nextElement, config).isExpanded &&
    !isExpandedDateRule(nextElement, config).isExpanded;
  // console.log('changing expanded to simple', isChangingFromExpandedToSimple);
  // console.log(
  //   'next data type',
  //   getFieldDataType(config, nextElement.els[0] as any)
  // );

  // Check if we're switching a date field off "isOn"/"isNotOn" to a plain
  // date operator (unlike string fields, date fields expose multiple
  // selectable operators alongside these two, so this same-field,
  // same-type change needs its own check)
  const isCollapsingExpandedDateRule =
    isRule(currentElement) &&
    !isRule(currentElement.els[0]) &&
    isExpandedDateRule(currentElement, config).isExpanded &&
    nextElement.els?.length === 1 &&
    !isRule(nextElement.els[0]) &&
    nextElement.els[0].k === currentElement.els[0].k &&
    !['isOn', 'isNotOn'].includes(nextElement.els[0].op);
  // Check if this is a special operator in an intentional group being changed to something (don't flatten)
  const unSetOperatorInGroup =
    isRule(currentElement) &&
    currentElement.type === 'any' &&
    currentElement.els?.length === 1 &&
    !isRule(currentElement.els[0]) &&
    ((usesSetOperators(getFieldDataType(config, currentElement.els[0])) &&
      ['isAnyOf', 'isNoneOf', 'containsAnyOf', 'doesNotContainAnyOf'].includes(
        currentElement.els[0].op
      )) ||
      (!isRule(nextElement.els[0]) &&
        !usesSetOperators(getFieldDataType(config, nextElement.els[0])) &&
        ['isBlank', 'isNotBlank', ''].includes(currentElement.els[0].op)));

  //console.log('unset operator in group', unSetOperatorInGroup);

  const isIntentionalGroupOnStringOperator =
    isRule(currentElement) &&
    currentElement.els?.length === 1 &&
    isRule(currentElement.els[0]) &&
    currentElement.els[0].els?.length > 0 &&
    !isRule(currentElement.els[0].els[0]) &&
    usesSetOperators(getFieldDataType(config, currentElement.els[0].els[0]));

  //console.log('intentional string group', isIntentionalGroupOnStringOperator);

  // Switching an expanded set row ("Manager is any of A, B") to a blank
  // operator on the same field ("Manager is not assigned"): the group of
  // scalar conditions collapses to that one condition.
  const isCollapsingSetRuleToBlankOperator =
    isRule(currentElement) &&
    !isRule(currentElement.els[0]) &&
    isExpandedStringRule(currentElement, config).isExpanded &&
    nextElement.els?.length === 1 &&
    !isRule(nextElement.els[0]) &&
    nextElement.els[0].k === currentElement.els[0].k &&
    isBlankOperator(nextElement.els[0].op);

  return (
    (isCollapsingExpandedStringRule ||
      isChangingFromExpandedToSimple ||
      isCollapsingExpandedDateRule ||
      isCollapsingSetRuleToBlankOperator) &&
    !unSetOperatorInGroup &&
    !isIntentionalGroupOnStringOperator
  );
};

/**
 * Handles changes to nested rule groups
 */
export const handleNestedRuleChange = <T extends string>(
  currentRule: Rule<T>,
  elementIndex: number,
  nextElement: Rule<T> | null,
  isRoot: boolean,
  config: RuleBuilderConfig<T>
): Rule<T> | null => {
  const nextEls = [...currentRule.els];

  if (nextElement === null) {
    nextEls.splice(elementIndex, 1);
    // If this was the last element in a non-root group, remove the entire group
    if (nextEls.length === 0 && !isRoot) {
      return null;
    }
  } else if (isRule(nextElement) && nextElement.els.length === 0) {
    // If a rule group becomes empty then remove the group
    nextEls.splice(elementIndex, 1);
    return {
      ...currentRule,
      els: nextEls
    };
  } else {
    const currentElement = currentRule.els[elementIndex];

    if (shouldFlattenRule(currentElement, nextElement, config)) {
      // Replace with just the condition, don't create nested groups
      nextEls[elementIndex] = nextElement.els[0];
    } else {
      nextEls[elementIndex] = nextElement;
    }
  }

  return {
    ...currentRule,
    els: nextEls
  };
};

/**
 * Handles changes to individual conditions within a rule
 */
export const handleConditionChange = <T extends string>(
  currentRule: Rule<T>,
  elementIndex: number,
  nextCondition: Condition<T> | null,
  isRoot: boolean,
  showEmptyRowWhenNull: boolean,
  config: RuleBuilderConfig<T>
): Rule<T> => {
  const nextEls = [...currentRule.els];

  if (nextCondition === null) {
    nextEls.splice(elementIndex, 1);
    if (nextEls.length === 0 && isRoot && showEmptyRowWhenNull) {
      nextEls.push(getEmptyCondition<T>());
    }
  } else {
    const fieldConfig = config[nextCondition.k];

    // Check if this should create an expanded rule
    const shouldExpandString =
      usesSetOperators(fieldConfig?.dataType) &&
      ['isAnyOf', 'isNoneOf', 'containsAnyOf', 'doesNotContainAnyOf'].includes(
        nextCondition.op
      ) &&
      Array.isArray(nextCondition.v) &&
      nextCondition.v.length > 0;

    const shouldExpandDate =
      fieldConfig?.dataType === 'date' &&
      ['isOn', 'isNotOn'].includes(nextCondition.op);

    if (shouldExpandString && Array.isArray(nextCondition.v)) {
      // Always create expanded rule, even for single values
      const expandedRule = createExpandedRule(
        nextCondition.k,
        nextCondition.op as StringOperator,
        nextCondition.v
      );
      nextEls[elementIndex] = expandedRule;
    } else if (shouldExpandDate && fieldConfig?.dataType === 'date') {
      nextEls[elementIndex] = ['isNotOn'].includes(nextCondition.op)
        ? createExpandedNotOnDateRule(
            nextCondition.k,
            nextCondition.v as number,
            fieldConfig.options.tz
          )
        : createExpandedDateRule(
            nextCondition.k,
            nextCondition.v as number,
            fieldConfig.options.tz
          );
    } else {
      nextEls[elementIndex] = nextCondition;
    }
  }

  return {
    ...currentRule,
    els: nextEls
  };
};

// ============================================================================
// SAVE-SHAPE UTILITIES
// ============================================================================

/**
 * The value list of a set-operator condition. A blank-operator condition
 * carries '' and yields an empty list, so nothing is rendered for it.
 */
export const toValueList = (v: MatchValue): string[] =>
  Array.isArray(v) ? v : v ? [`${v}`] : [];

/**
 * True when the rule holds a condition the backend would reject: a set
 * operator that has not been expanded yet (no values picked), or an array
 * value on a scalar operator. Blank operators ignore their value and are
 * always complete.
 */
export const hasIncompleteCondition = <T extends string>(
  node: Rule<T> | Condition<T>
): boolean => {
  if (isRule(node)) return node.els.some(hasIncompleteCondition);
  if (isBlankOperator(node.op)) return false;
  return (
    !(OPERATORS as readonly string[]).includes(node.op) || Array.isArray(node.v)
  );
};

/**
 * Blank operators ignore their value; store '' for them so the saved shape
 * is always a scalar, whatever the editor left behind.
 */
export const normalizeBlankOperatorValues = <T extends string>(
  rule: Rule<T>
): Rule<T> => ({
  ...rule,
  els: rule.els.map((el) =>
    isRule(el)
      ? normalizeBlankOperatorValues(el)
      : isBlankOperator(el.op)
      ? { ...el, v: '' }
      : el
  )
});

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

export const formatValue = <T extends string>(
  k: T,
  v: MatchValue,
  conf: RuleBuilderConfig<T>
): string => {
  const c = conf[k];

  if (c.dataType === 'user') {
    return toValueList(v)
      .map((id) =>
        id === BLANK_VALUE ? BLANK_LABEL_USER : c.options.formatValue(id)
      )
      .join(', ');
  }

  if (c.dataType === 'string' && Array.isArray(v)) {
    return v
      .map((val) => (val === BLANK_VALUE ? BLANK_LABEL_STRING : val))
      .join(', ');
  }

  if (c.dataType === 'date') {
    // A blank operator carries '' as its value; render nothing for it.
    const millis = parseInt(v as any, 10);
    if (Number.isNaN(millis)) return '';
    // The value is the start of the chosen day in the field's zone; render it
    // in that zone (not the browser's) so the day never shifts for a viewer
    // elsewhere, and in the org's date format.
    return moment
      .tz(millis, c.options.tz)
      .format(c.options.dateFormat ?? DEFAULT_DATE_FORMAT);
  }
  return `${v}`;
};

export const formatEl = <T extends string>(
  el: Condition<T>,
  conf: RuleBuilderConfig<T>,
  t: TFunction
): string => {
  const { k, v, op } = el;
  const { label, dataType } = conf[k];

  // Blank operators ignore their value, and a rule saved before values were
  // normalised may still carry a placeholder (a date field defaulted to Date.now()).
  if (isBlankOperator(op)) {
    return `${label} ${t(OPERATORS_TO_STRING[dataType][op])}`;
  }

  // Handle set operators (string and user fields)
  if (
    usesSetOperators(dataType) &&
    ['isAnyOf', 'isNoneOf', 'containsAnyOf', 'doesNotContainAnyOf'].includes(op)
  ) {
    const values = Array.isArray(v) ? v : [v as string];
    const valueStr = formatValue(k, values as unknown as MatchValue, conf);
    return `${label} ${t(OPERATORS_TO_STRING[dataType][op])} ${valueStr}`;
  }

  return `${label} ${t(OPERATORS_TO_STRING[dataType][op])} ${formatValue(
    k,
    v,
    conf
  )}`;
};

/**
 * Text for one condition. A key with no config entry (a field removed from
 * the org since the rule was saved) falls back to the raw key, operator and
 * value rather than throwing.
 */
export const formatCondition = <T extends string>(
  condition: Condition<T>,
  conf: RuleBuilderConfig<T>,
  t: TFunction
): string => {
  const { k, v, op } = condition;
  if (!conf[k]) {
    return `${k} ${op} ${v}`;
  }
  return formatEl(condition, conf, t);
};

/**
 * The rule as text, structured like the rule: one string per condition, with
 * genuine nested groups kept as nested elements. A group that is really one
 * expanded set condition ("X is any of A, B") or one expanded date condition
 * ("D is on <day>") is collapsed back to a single string, so the text reads
 * the way the condition was authored. This is the single source for every
 * rendering of a rule: the React renderer's one-line and inline variants,
 * and the plain-text form.
 */
export function formatRule<T extends string>(
  rule: Rule<T>,
  conf: RuleBuilderConfig<T>,
  t: TFunction
): FormattedRuleElement {
  return {
    type: rule.type,
    els: rule.els.map((el) => {
      if (!isRule(el)) {
        return formatCondition(el, conf, t);
      }
      const expanded = isExpandedStringRule(el, conf);
      if (expanded.isExpanded && expanded.key && expanded.operator && expanded.values) {
        return formatCondition(
          {
            k: expanded.key,
            op: expanded.operator as Operator,
            v: expanded.values as unknown as MatchValue
          },
          conf,
          t
        );
      }
      const expandedDate = isExpandedDateRule(el, conf);
      if (expandedDate.isExpanded && expandedDate.key && expandedDate.value !== undefined) {
        return formatCondition(
          {
            k: expandedDate.key,
            op: expandedDate.operator as Operator,
            v: expandedDate.value
          },
          conf,
          t
        );
      }
      return formatRule(el, conf, t);
    })
  };
}

/**
 * Plain-text equivalent of RuleRenderer's "oneline" output (same traversal,
 * same " and "/" or " separators and parens around nested groups), for
 * callers that need to measure or compare the text rather than render it.
 */
const stringifyFormattedRule = (v: FormattedRuleElement): string => {
  const parts = v.els.map((el) =>
    typeof el === 'string' ? el : `(${stringifyFormattedRule(el)})`
  );
  return parts.join(v.type === 'all' ? ' and ' : ' or ');
};

export const formatRuleAsPlainText = <T extends string>(
  rule: Rule<T>,
  conf: RuleBuilderConfig<T>,
  t: TFunction
): string => stringifyFormattedRule(formatRule(rule, conf, t));

/**
 * How a condition row renders for the editor's field config. A rule can reference a field
 * this editor doesn't offer (a removed custom field, or a rule written outside the editor —
 * e.g. by the AI assistant). That must NOT throw: it used to take the whole template page
 * down. It resolves as `isUnknownField`, and the row shows a notice so the admin can pick a
 * field or remove the condition. Pure — unit-tested.
 */
export const resolveConditionField = <T extends string>(value: Condition<T>, config: RuleBuilderConfig<T>) => {
  const { k, op } = value;
  const isEmptyCondition = k === '';
  const c: RuleBuilderConfigItem<T> | null = isEmptyCondition ? null : config[k] ?? null;
  const isUnknownField = !isEmptyCondition && !c;
  const onlyBlankOperators =
    !!c?.options?.allowedOperators?.includes('isBlank') && !!c?.options?.allowedOperators?.includes('isNotBlank');
  const showOperatorField = !isEmptyCondition && !isUnknownField;
  const showValueField =
    showOperatorField && op !== ('' as Operator) && !onlyBlankOperators && op !== 'isBlank' && op !== 'isNotBlank';
  const operators = c ? c.options?.allowedOperators || OPERATORS_BY_DATA_TYPE[c.dataType] : [];
  return { c, isEmptyCondition, isUnknownField, showOperatorField, showValueField, operators };
};
